import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Briefcase, MapPin, Calendar } from 'lucide-react';
import { experienceData } from '../data/mock';

// Warm color mapping - no blue, violet, or green
const colorMap = {
  '#d987ff': '#f4b8c5',  // Soft rose
  '#88a2ff': '#e8c4c4',  // Dusty rose
  '#78d692': '#f5d89a',  // Soft gold
  '#e6a87c': '#f0c4a8',  // Soft amber
  '#d4a5a5': '#e8c4c4',  // Dusty rose
  '#f4b8c5': '#f4b8c5'   // Soft rose
};

const getGentleColor = (color) => colorMap[color] || color;

const TimelineItem = ({ experience, index, isInView, isLast }) => {
  const gentleColor = getGentleColor(experience.color);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.2 }}
      className={`relative flex items-center gap-8 ${
        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
      }`}
    >
      {/* Content Card */}
      <motion.div
        whileHover={{ y: -4, scale: 1.01 }}
        className="flex-1 group"
      >
        <div
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-transparent relative overflow-hidden backdrop-blur-sm"
        >
          {/* Hover Gradient Overlay */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${gentleColor}12, transparent 70%)`
            }}
          />

          <div className="relative z-10">
            {/* Type Badge */}
            <span
              className="inline-block px-3 py-1 text-xs font-medium rounded-full mb-4"
              style={{
                background: `linear-gradient(135deg, ${gentleColor}20, ${gentleColor}10)`,
                color: gentleColor
              }}
            >
              {experience.type}
            </span>

            {/* Role & Company */}
            <h3 className="text-xl md:text-2xl font-semibold text-white mb-2">
              {experience.role}
            </h3>
            <p className="text-lg font-medium mb-4" style={{ color: gentleColor }}>
              {experience.company}
            </p>

            {/* Description */}
            <p className="text-white/50 mb-4">
              {experience.description}
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-white/40">
              <div className="flex items-center gap-2">
                <Calendar size={14} />
                <span>{experience.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={14} />
                <span>{experience.location}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Timeline Node */}
      <div className="hidden md:flex flex-col items-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ duration: 0.4, delay: index * 0.2 + 0.3 }}
          className="w-5 h-5 rounded-full border-4 z-10"
          style={{
            backgroundColor: '#0d0d0f',
            borderColor: gentleColor,
            boxShadow: `0 0 20px ${gentleColor}40`
          }}
        />
        {!isLast && (
          <motion.div
            initial={{ height: 0 }}
            animate={isInView ? { height: '100px' } : {}}
            transition={{ duration: 0.6, delay: index * 0.2 + 0.4 }}
            className="w-0.5"
            style={{ background: `linear-gradient(to bottom, ${gentleColor}40, transparent)` }}
          />
        )}
      </div>

      {/* Spacer for alternating layout */}
      <div className="hidden md:block flex-1" />
    </motion.div>
  );
};

const ExperienceTimeline = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });

  const lineHeight = useTransform(scrollYProgress, [0, 0.5], ['0%', '100%']);

  return (
    <section id="experience" ref={sectionRef} className="py-32 bg-[#0d0d0f] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#f5d89a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#e8c4c4]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f5d89a]/20 to-[#a5d8ff]/10 border border-[#f5d89a]/20 flex items-center justify-center">
              <Briefcase className="text-[#f5d89a]" size={24} />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            My Journey
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            A timeline of my professional experience and growth
          </p>
        </motion.div>

        {/* Chat Bubble */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex gap-4 items-start max-w-2xl mx-auto mb-16"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ffb79c] to-[#f5d89a] flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-[#0d0d0f]">SP</span>
          </div>
          <div className="bg-gradient-to-r from-[#ffb79c]/10 to-[#f5d89a]/5 border border-[#ffb79c]/15 rounded-2xl rounded-tl-md px-5 py-3 backdrop-blur-sm">
            <p className="text-white/80">
              Let me walk you through my professional journey. Each role has shaped me into the engineer I am today.
            </p>
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Animated Timeline Line - Hidden on mobile */}
          <motion.div
            className="hidden md:block absolute left-1/2 top-0 w-0.5 origin-top"
            style={{ 
              height: lineHeight,
              background: 'linear-gradient(to bottom, #f4b8c540, #e8c4c440, #f5d89a40)'
            }}
          />

          {/* Timeline Items */}
          <div className="space-y-8 md:space-y-0">
            {experienceData.map((experience, index) => (
              <TimelineItem
                key={experience.id}
                experience={experience}
                index={index}
                isInView={isInView}
                isLast={index === experienceData.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceTimeline;
